import { useEffect, useId, useRef, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography } from "@mui/material";
import { sceneShadingPending } from "../config/sceneSimulationIssues";

/**
 * Asked when a run starts and the layout's shading is missing or out of date:
 * the two honest ways on -- calculate it now (stored with the layout, as the
 * layout's own "Calculate shading" stores it) and carry on, or run without it,
 * by choice. Nothing is worked out silently on the way to a run.
 *
 * Modal on purpose: while the calculation runs the rest of the application is
 * behind it, so nothing can lead away from the result before it is stored.
 * Its only control then is to stop the calculation.
 */
export default function LayoutShadingDialog({
  open, config, isGreek = false, onClose,
  // (({ onProgress, shouldCancel }) => Promise<config>): calculates and stores.
  onCalculate = null,
  // (config) => void: after a calculation, with the project as it now stands.
  onContinue = null,
  onRunWithout = null,
  calculateLabel = null,
  withoutLabel = null,
}) {
  const tr = (el, en) => (isGreek ? el : en);
  const titleId = useId();
  const [state, setState] = useState(null);   // { progress } while calculating, { error } after a failure
  const cancelRef = useRef(false);
  useEffect(() => { if (!open) setState(null); }, [open]);
  const pending = config ? sceneShadingPending(config) : [];
  const calculating = state?.progress != null;
  const which = pending.length > 1
    ? tr(`προσανατολισμοί ${pending.join(", ")}`, `orientations ${pending.join(", ")}`)
    : tr(`προσανατολισμός ${pending[0] ?? 1}`, `orientation ${pending[0] ?? 1}`);
  const calculate = async () => {
    cancelRef.current = false;
    setState({ progress: 0 });
    try {
      const next = await onCalculate({
        onProgress: (p) => { if (!cancelRef.current) setState({ progress: p }); },
        shouldCancel: () => cancelRef.current,
      });
      setState(null);
      if (!cancelRef.current && onContinue) onContinue(next);
    } catch (err) {
      setState(cancelRef.current || err?.cancelled ? null : { error: err?.message || String(err) });
    }
  };
  return (
    <Dialog open={open} onClose={calculating ? undefined : onClose} disableEscapeKeyDown={calculating}
      // Wide enough for the three choices on one line; they wrap on a phone.
      maxWidth={false} PaperProps={{ sx: { width: "min(660px, calc(100% - 32px))" } }} aria-labelledby={titleId}>
      <DialogTitle id={titleId}>{tr("Σκιάσεις διάταξης", "Layout shading")}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" role="status" sx={{ fontWeight: 600, mb: 1 }}>
          {calculating
            ? tr("Υπολογισμός σκιάσεων…", "Calculating shading…")
            : tr(`Οι σκιάσεις της διάταξης δεν έχουν υπολογιστεί ή δεν είναι ενημερωμένες (${which}).`,
              `The layout shading has not been calculated or is out of date (${which}).`)}
        </Typography>
        {calculating
          ? <LinearProgress variant="determinate" value={Math.round(state.progress * 100)}
            aria-label={tr("Πρόοδος υπολογισμού σκιάσεων", "Shading calculation progress")} sx={{ mt: 1.5, mb: 0.5 }} />
          : <Typography variant="body2" color="text.secondary">
            {tr("Με τον υπολογισμό, οι σκιάσεις αποθηκεύονται μαζί με τη διάταξη, όπως με τον «Υπολογισμό σκιάσεων» της φυσικής διάταξης, και η εκτέλεση συνεχίζει αμέσως· αποθηκεύστε μετά το έργο. Χωρίς σκιάσεις διάταξης παραμένει η τυπική σκίαση μεταξύ σειρών, αλλά παραλείπονται τα εμπόδια, οι άλλες πλευρές και η σκίαση ανά string.",
              "Calculating stores the shading with the layout, as “Calculate shading” in the physical layout does, and the run carries on straight away; save the project afterwards. Without layout shading, standard row shading remains, but obstacle, other-surface and per-string shading are omitted.")}
          </Typography>}
        {state?.error && <Alert severity="error" sx={{ mt: 1.5 }}>{state.error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 0.5 }}>
        {calculating
          ? <Button onClick={() => { cancelRef.current = true; }} sx={{ textTransform: "none" }}>
            {tr("Ακύρωση υπολογισμού", "Cancel calculation")}
          </Button>
          : <>
            <Button onClick={onClose} sx={{ textTransform: "none", mr: "auto" }}>{tr("Άκυρο", "Cancel")}</Button>
            {onRunWithout && <Button onClick={onRunWithout} sx={{ textTransform: "none" }}>
              {withoutLabel || tr("Συνέχεια χωρίς σκιάσεις διάταξης", "Continue without layout shading")}
            </Button>}
            {onCalculate && <Button variant="contained" onClick={calculate} sx={{ textTransform: "none" }}>
              {calculateLabel || tr("Υπολογισμός σκιάσεων και συνέχεια", "Calculate shading and continue")}
            </Button>}
          </>}
      </DialogActions>
    </Dialog>
  );
}
